import { NgZone } from '@angular/core';

const GAP_PX = 10;

// Eased, backlog-proportional velocity (px per 60fps-frame). Speed ramps up
// smoothly when messages pile up and eases back as the row catches up — no
// discrete steps, so the motion never visibly jerks. V_MAX is high enough to
// keep pace with a busy chat.
const V_MIN = 2;             // gentle glide for the last card
const V_MAX = 50;            // burst-clear cap (fast, but never a teleport)
const K_PROPORTIONAL = 0.012; // targetV = backlog * K, clamped to [V_MIN, V_MAX]
const SMOOTHING = 0.1;       // currentV += (targetV - currentV) * SMOOTHING
const STOP_BEHIND = 0.5;     // px backlog below which we consider it caught up
const STOP_V = 0.05;         // velocity below which we settle and idle

// Soft buffer cap. When chat outruns the scroll, drop the oldest cards so the
// DOM stays small. Spread the drops over frames so a giant burst can't cause a
// single-frame detectChanges storm.
const SOFT_CAP = 25;
const MAX_TRIM_PER_FRAME = 6;

// A rAF gap longer than this means the page was frozen — almost always an OBS
// source toggled hidden→shown (OBS pauses rAF for hidden sources). Messages
// keep arriving over the socket while frozen, so on resume we drop that backlog
// and show only the most recent few instead of flooding the scene.
const RESUME_GAP_MS = 1000;

/**
 * Transform-based horizontal marquee for the live-chat overlay.
 *
 * The message row (`track`) is moved left with `translate3d` — a
 * compositor/GPU transform that stays smooth under main-thread load, unlike
 * per-frame `scrollLeft` writes. New messages append on the right; the
 * controller removes leading cards (each measured individually, so cards may
 * have different widths) once they scroll off the left, or force-drops the
 * oldest when the buffer exceeds SOFT_CAP. Every removal shrinks the translate
 * offset by exactly the removed width, applied in the SAME frame (a forced
 * `flush()` synchronises the DOM), so on-screen cards never jump.
 *
 * Layout is measured only when the card count changes, on resize, or while
 * trimming — never every idle frame — to avoid forced reflow.
 */
export class LiveChatScrollController {
  private rafId: number | null = null;
  private offsetX = 0;          // px the track is translated left
  private velocity = 0;         // eased px/frame
  private maxX = 0;             // offset that pins the newest card to the right edge
  private lastChildCount = -1;
  private boundsDirty = true;
  private lastFrameTime = 0;
  private readonly onResize = () => { this.boundsDirty = true; };

  constructor(
    private readonly zone: NgZone,
    private readonly getTrack: () => HTMLElement | undefined,
    private readonly getViewport: () => HTMLElement | undefined,
    private readonly trimLeading: () => boolean,
    /** Synchronously flush Angular's pending DOM (e.g. ChangeDetectorRef.detectChanges). */
    private readonly flush: () => void,
    /** On resume after a freeze (OBS source shown): keep the `head` cards that
     *  were already on screen and drop the hidden backlog except its newest
     *  few, which then scroll in normally. */
    private readonly onResume: (head: number) => void,
  ) {}

  start(): void {
    this.stop();
    this.boundsDirty = true;
    this.lastFrameTime = 0;
    window.addEventListener('resize', this.onResize, { passive: true });
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame((t) => this.tick(t));
    });
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    window.removeEventListener('resize', this.onResize);
  }

  private tick(time: number): void {
    this.rafId = requestAnimationFrame((t) => this.tick(t));

    const track = this.getTrack();
    const viewport = this.getViewport();
    if (!track || !viewport) return;

    const prev = this.lastFrameTime;
    this.lastFrameTime = time;
    const rawGap = prev ? time - prev : 0;
    const resumed = rawGap > RESUME_GAP_MS;

    if (resumed) {
      // Page just un-froze (OBS source shown). Keep the cards that were already
      // on screen and drop the backlog that piled up while hidden EXCEPT its
      // newest few — those then scroll in from the right with the normal
      // animation (continuity, no flood). `head` = cards up to the right edge
      // of the current viewport; everything past it is the hidden backlog.
      const viewportRight = this.offsetX + viewport.clientWidth;
      let cum = 0;
      let head = 0;
      for (let i = 0; i < track.children.length; i++) {
        if (cum > viewportRight) break;
        head = i + 1;
        cum += (track.children[i] as HTMLElement).offsetWidth + GAP_PX;
      }
      this.onResume(head);
      this.flush();
      this.recomputeBounds(track, viewport);
      this.lastChildCount = track.childElementCount;
      // offsetX is left unchanged: the on-screen cards stay put (we only removed
      // cards off to their right), and the newest few scroll in next.
      this.velocity = 0;
      this.applyTransform(track);
      return;
    }

    // Re-measure max scroll only when the card set changed or after a resize.
    const childCount = track.childElementCount;
    if (this.boundsDirty || childCount !== this.lastChildCount) {
      this.recomputeBounds(track, viewport);
      this.lastChildCount = childCount;
    }

    // Frame-rate-independent step, normalised to 60fps and clamped so a long
    // stall can't produce a giant jump (and is forced to 1 right after a resume).
    const dt = !prev || resumed ? 1 : Math.min(2, rawGap / 16.6667);

    const behind = this.maxX - this.offsetX;

    const targetV =
      behind <= STOP_BEHIND ? 0 : Math.min(V_MAX, Math.max(V_MIN, behind * K_PROPORTIONAL));
    this.velocity += (targetV - this.velocity) * SMOOTHING;

    if (behind <= STOP_BEHIND && this.velocity < STOP_V) {
      // Caught up — settle exactly on the newest card and idle cheaply until
      // the next message bumps the child count (no setTimeout gap, no jerk).
      if (this.offsetX !== this.maxX) {
        this.offsetX = this.maxX;
        this.applyTransform(track);
      }
      this.velocity = 0;
      return;
    }

    this.offsetX = Math.min(this.maxX, this.offsetX + this.velocity * dt);
    this.applyTransform(track);
    this.trimRow(track);
  }

  private recomputeBounds(track: HTMLElement, viewport: HTMLElement): void {
    this.maxX = Math.max(0, track.scrollWidth - viewport.clientWidth);
    this.boundsDirty = false;
  }

  private applyTransform(track: HTMLElement): void {
    track.style.transform = `translate3d(${-this.offsetX}px, 0, 0)`;
  }

  /**
   * Remove leading cards that have scrolled off the left, plus force-drop the
   * oldest while over SOFT_CAP. Each card is measured individually (variable
   * widths), the removals are batched into a single flush, and the offset is
   * pulled back by the exact total removed width — so nothing jumps.
   */
  private trimRow(track: HTMLElement): void {
    const first = track.firstElementChild as HTMLElement | null;
    if (!first) return;

    // Fast exit when there's nothing to trim.
    if (this.offsetX < first.offsetWidth + GAP_PX && track.childElementCount <= SOFT_CAP) {
      return;
    }

    let removedWidth = 0;
    let removed = 0;
    let cumulative = 0;
    let remaining = track.childElementCount;

    while (removed < MAX_TRIM_PER_FRAME && remaining > 8) {
      const card = track.children[removed] as HTMLElement | undefined;
      if (!card) break;

      const w = card.offsetWidth + GAP_PX;
      cumulative += w;
      const offScreen = this.offsetX >= cumulative; // card's right edge past the left edge
      const overCap = remaining > SOFT_CAP;
      if (!offScreen && !overCap) break;

      if (!this.trimLeading()) break; // service min floor reached
      removedWidth += w;
      removed++;
      remaining--;
    }

    if (removed === 0) return;

    this.flush(); // one synchronous render for all removals this frame
    this.offsetX = Math.max(0, this.offsetX - removedWidth);
    this.maxX = Math.max(0, this.maxX - removedWidth);
    this.lastChildCount = track.childElementCount;
    this.applyTransform(track);
  }
}
